import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import SearchField from "@components/ui/textfields/SearchField";
import { useProjectStore } from "@store/projectStore";
import { useState, useMemo } from "react";
import { PartnerInfos } from "types";
import { CreatePartnerModal } from "./CreatePartnerModal";
import {
  COMPANY_TYPE_LABELS,
  COMPANY_TYPE_OPTIONS,
  COMPANY_TYPE_EXPANSIONS,
  HIDDEN_COMPANY_TYPES,
  partnerMatchesType,
} from "@constants/partnerTypes";

export const AddPartnerToProjectModal = ({
  onClose,
  onSelect,
  existingPartnerIds,
  filterByType,
  categoryOptions = COMPANY_TYPE_OPTIONS,
}: {
  onClose: () => void;
  onSelect: (partner: PartnerInfos) => void;
  existingPartnerIds: string[];
  filterByType?: string;
  // Category list for the "Dans quelle catégorie ajouter ?" picker. Defaults
  // to the base taxonomy; the project create/modify modals pass the extended
  // list (PROJECT_PARTNER_CATEGORY_OPTIONS) so their project-only categories
  // (Paysagiste, Pisciniste, Étude béton…) appear there too — and nowhere else.
  categoryOptions?: { value: string; label: string }[];
}) => {
  const partners = useProjectStore((state) => state.partners);
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingPartner, setPendingPartner] = useState<PartnerInfos | null>(null);
  const filterLabel = filterByType
    ? COMPANY_TYPE_LABELS[filterByType] || filterByType
    : "";

  // Partners filtered by category (no pagination — full scrollable list).
  // Uses `partnerMatchesType` so virtual expansions are honoured: e.g. a
  // partner tagged only "gros_oeuvre" surfaces in the Terrassement section,
  // a "plomberie" partner surfaces in the Chauffage section, etc.
  const categoryPartners = useMemo(() =>
    partners
      .filter((p) => !existingPartnerIds.includes(p.id))
      .filter((p) => !filterByType || partnerMatchesType(p.company_types, filterByType))
      .sort((a, b) =>
        (a.company_name || a.display_name || "").localeCompare(
          b.company_name || b.display_name || "",
        ),
      ),
    [partners, existingPartnerIds, filterByType],
  );

  // Search across ALL partners (ignoring category filter)
  const searchResults = useMemo(() => {
    if (!search) return [];
    const s = search.toLowerCase();
    return partners
      .filter((p) => !existingPartnerIds.includes(p.id))
      .filter(
        (p) =>
          p.display_name?.toLowerCase().includes(s) ||
          p.company_name?.toLowerCase().includes(s),
      )
      .sort((a, b) =>
        (a.company_name || a.display_name || "").localeCompare(
          b.company_name || b.display_name || "",
        ),
      );
  }, [partners, existingPartnerIds, search]);

  const displayed = search ? searchResults : categoryPartners;

  const handlePartnerClick = (partner: PartnerInfos) => {
    // When added via a specific category section, the project copy of the
    // partner should carry ONLY that category — otherwise the partner shows
    // up in every column matching their org-wide types.
    if (filterByType) {
      const partnerTypes = partner.company_types || [];
      const directMatch = partnerTypes.includes(filterByType);
      let scopedType = filterByType;
      if (!directMatch) {
        // No direct match: the partner is offered here via an expansion
        // (e.g. gros_oeuvre under Terrassement). Keep their original tag as the
        // base — the parent's onSelect materializes the section (filterByType)
        // on top, so the partner ends up a real member of BOTH sections.
        const expansion = COMPANY_TYPE_EXPANSIONS[filterByType] || [];
        const matchedSource = expansion.find((t) => partnerTypes.includes(t));
        if (matchedSource) scopedType = matchedSource;
      }
      onSelect({ ...partner, company_types: [scopedType] });
      onClose();
      return;
    }
    // Global "Ajouter un intervenant" flow (no section pre-selected) — always
    // force a category pick. Without this, partners whose only types are now
    // hidden would be added without any visible section tag and stay invisible
    // in PartnersSection (which iterates COMPANY_TYPE_OPTIONS).
    setPendingPartner(partner);
  };

  const handleCreateAndSelect = (partner: PartnerInfos) => {
    handlePartnerClick(partner);
  };

  return (
    <>
      {showCreateModal && (
        <CreatePartnerModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateAndSelect}
        />
      )}
      <BaseModal
        title="Ajouter un intervenant"
        startIcon={<Icon1 name="expert" size={20} />}
        onClose={onClose}
      >
        <div className="flex flex-col gap-3 p-4">
          {filterByType && (
            <div className="text-h3 font-semibold text-neutral-600 bg-neutral-100 px-3 py-2 rounded-lg">
              Catégorie : {filterLabel}
            </div>
          )}
          <SearchField
            value={search}
            onChange={(value) => setSearch(value)}
            focusOnStart
            placeholder={search ? "Recherche dans toutes les catégories..." : "Rechercher un intervenant..."}
          />
          {displayed.length > 0 && (
            <div className="text-h4 text-neutral-400">
              {displayed.length} intervenant{displayed.length > 1 ? "s" : ""}{" "}
              {search ? "trouvé" : "disponible"}{displayed.length > 1 ? "s" : ""}
            </div>
          )}
          <div className="flex flex-col gap-1 max-h-300 overflow-y-auto">
            {!existingPartnerIds.includes("maitre_ouvrage") && (
              <div
                onClick={() => {
                  const maitreOuvrage: PartnerInfos = {
                    id: "maitre_ouvrage",
                    display_name: "Maître d'ouvrage",
                    company_name: "Maître d'ouvrage",
                    company_types: [],
                  };
                  if (filterByType) {
                    onSelect({ ...maitreOuvrage, company_types: [filterByType] });
                    onClose();
                  } else {
                    // Global flow (no section pre-selected): route through the
                    // same category picker so the user chooses which lot it applies to.
                    setPendingPartner(maitreOuvrage);
                  }
                }}
                className="flex flex-row items-center gap-3 px-4 py-3 rounded-lg bg-neutral-50 hover:bg-neutral-100 cursor-pointer border border-dashed border-neutral-300 transition-colors"
              >
                <div className="flex flex-col flex-1">
                  <div className="text-h2 text-black font-medium">
                    Maître d'ouvrage
                  </div>
                  <div className="text-h3 text-neutral-400">
                    Travaux réalisés par le client
                  </div>
                </div>
                <Icon1 name="plus" size={16} />
              </div>
            )}
            {displayed.length === 0 && (
              <div className="text-h3 text-neutral-400 text-center py-4">
                Aucun intervenant trouvé
              </div>
            )}
            {displayed.map((partner) => (
              <div
                key={partner.id}
                onClick={() => handlePartnerClick(partner)}
                className="flex flex-row items-center gap-3 px-4 py-3 rounded-lg bg-white hover:bg-neutral-50 cursor-pointer border transition-colors"
              >
                <div className="flex flex-col flex-1 gap-0.5">
                  <div className="text-h2 text-black font-medium">
                    {partner.company_name || partner.display_name}
                  </div>
                  <div className="text-h3 text-neutral-400">
                    {partner.company_name && partner.display_name
                      ? partner.display_name
                      : ""}
                  </div>
                  {search && partner.company_types && partner.company_types.filter((t) => !HIDDEN_COMPANY_TYPES.has(t)).length > 0 && (
                    <div className="flex flex-row flex-wrap gap-1 mt-1">
                      {partner.company_types
                        .filter((type) => !HIDDEN_COMPANY_TYPES.has(type))
                        .map((type) => (
                          <span
                            key={type}
                            className="text-h5 px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500"
                          >
                            {COMPANY_TYPE_LABELS[type] || type}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
                <Icon1 name="plus" size={16} />
              </div>
            ))}
          </div>
          {pendingPartner && (
            <div className="flex flex-col gap-2 border rounded-xl p-4 bg-neutral-50">
              <div className="text-h3 font-semibold text-neutral-700">
                Dans quelle catégorie ajouter {pendingPartner.company_name || pendingPartner.display_name} ?
              </div>
              <div className="flex flex-wrap gap-2">
                {categoryOptions.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      onSelect({ ...pendingPartner, company_types: [type.value] });
                      onClose();
                    }}
                    className="px-3 py-2 rounded-lg bg-white border border-neutral-200 text-h3 font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
                  >
                    {type.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setPendingPartner(null)}
                className="text-h4 text-neutral-400 hover:text-neutral-600 self-start"
              >
                Annuler
              </button>
            </div>
          )}
          <div className="border-t border-neutral-200 pt-3">
            <SecondaryButton
              startIcon={<Icon1 name="plus" size={16} />}
              label="Créer un intervenant"
              variant="large"
              onClick={() => setShowCreateModal(true)}
            />
          </div>
        </div>
      </BaseModal>
    </>
  );
};
